import { useState } from 'react';
import './ActivityMode.css';

function formatDateTime(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function formatDateOnly(dateString) {
  if (!dateString) return '';
  return new Date(dateString).toISOString().slice(0, 10);
}

function getActionIcon(action) {
  const icons = {
    CLIENT_CREATED: '🌱',
    TICKED_ITEM: '✓',
    UNTICKED_ITEM: '○',
    STAGE_SUBMITTED: '📤',
    STAGE_APPROVED: '✅',
    STAGE_REJECTED: '✗',
    ITEM_APPROVED: '✅',
    ITEM_REJECTED: '✗',
    NOTE_ADDED: '📝',
    ASSIGNED: '👤'
  };
  return icons[action] || '●';
}

function getActionDotClass(action) {
  if (action === 'STAGE_APPROVED' || action === 'ITEM_APPROVED' || action === 'TICKED_ITEM') {
    return 'cj-activity__dot cj-activity__dot--green';
  }
  if (action === 'STAGE_REJECTED' || action === 'ITEM_REJECTED') {
    return 'cj-activity__dot cj-activity__dot--red';
  }
  if (action === 'STAGE_SUBMITTED') {
    return 'cj-activity__dot cj-activity__dot--amber';
  }
  return 'cj-activity__dot';
}

export default function ActivityMode({ activityLog, users }) {
  const [searchText, setSearchText] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  function getAuthorName(userId) {
    if (!users || !userId) return '—';
    const user = users.find(u => u._id === userId);
    return user ? user.name : userId;
  }

  function getFilteredLog() {
    if (!activityLog) return [];

    let entries = [...activityLog].sort((a, b) => {
      if (!a.timestamp) return 1;
      if (!b.timestamp) return -1;
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      entries = entries.filter(e => {
        const detail = (e.detail || '').toLowerCase();
        const action = (e.action || '').toLowerCase();
        const user = getAuthorName(e.userId).toLowerCase();
        return detail.includes(lower) || action.includes(lower) || user.includes(lower);
      });
    }

    if (fromDate) {
      const from = new Date(fromDate);
      from.setHours(0, 0, 0, 0);
      entries = entries.filter(e => e.timestamp && new Date(e.timestamp) >= from);
    }

    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      entries = entries.filter(e => e.timestamp && new Date(e.timestamp) <= to);
    }

    return entries;
  }

  function handleClearFilters() {
    setSearchText('');
    setFromDate('');
    setToDate('');
  }

  const filtered = getFilteredLog();
  const hasFilters = searchText.trim() || fromDate || toDate;

  return (
    <div className="cj-activity">
      <div className="cj-activity__toolbar">
        <input
          className="cj-activity__search"
          type="text"
          placeholder="Search activity..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
        <div className="cj-activity__date-range">
          <label className="cj-activity__date-label">From</label>
          <input
            className="cj-activity__date-input"
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
          />
          <label className="cj-activity__date-label">To</label>
          <input
            className="cj-activity__date-input"
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
          />
        </div>
        {hasFilters && (
          <button className="cj-activity__clear-btn" onClick={handleClearFilters}>
            Clear
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="cj-activity__empty">
          <p>{hasFilters ? 'No activity matches your filters.' : 'No activity recorded yet.'}</p>
        </div>
      ) : (
        <div className="cj-activity__list">
          {filtered.map((entry, index) => (
            <div key={index} className="cj-activity__entry">
              <div className={getActionDotClass(entry.action)}>
                <span className="cj-activity__icon">{getActionIcon(entry.action)}</span>
              </div>
              <div className="cj-activity__content">
                <span className="cj-activity__detail">{entry.detail}</span>
                <div className="cj-activity__meta">
                  <span className="cj-activity__user">{getAuthorName(entry.userId)}</span>
                  <span className="cj-activity__time">{formatDateTime(entry.timestamp)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
